import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Lock, EnvelopeSimple, User, ShieldCheck, ChartBar, CreditCard, Buildings } from "@phosphor-icons/react";

const QUICK = [
  { role: "super_admin", email: "admin@cpcc.local", label: "Admin", icon: User },
  { role: "security_guard", email: "guard@cpcc.local", label: "Guard", icon: ShieldCheck },
  { role: "owner", email: "owner@cpcc.local", label: "Owner", icon: Buildings },
  { role: "finance_officer", email: "finance@cpcc.local", label: "Finance", icon: ChartBar },
];

const FEATURES = [
  {
    title: "AI Plate Recognition",
    desc: "Camera and ALPR-ready architecture",
    icon: ShieldCheck,
  },
  {
    title: "Revenue Sharing",
    desc: "Owner, Condo and Manager settlements",
    icon: ChartBar,
  },
  {
    title: "Digital Payments",
    desc: "QRPH, GCash and automated billing",
    icon: CreditCard,
  },
  {
    title: "Multi-Property Ready",
    desc: "Condominiums, offices and mixed-use estates",
    icon: Buildings,
  },
];

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@cpcc.local");
  const [password, setPassword] = useState("Password@123");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e, override) => {
    e?.preventDefault?.();
    const em = override?.email || email;
    const pw = override?.password || password;

    try {
      await login(em, pw);
      navigate("/");
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#FBFAF5]">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/amaia-bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-r from-white/96 via-white/90 to-white/76" />
      <div className="absolute inset-y-0 right-0 w-[28%] bg-white/70 backdrop-blur-sm hidden lg:block" />

      <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_0.9fr]">
        <section className="px-8 sm:px-12 lg:px-20 py-10 flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="mb-8">
              <img
                src="/amaia-logo.png"
                alt="Amaia Steps"
                className="h-20 w-auto object-contain mb-5"
              />

              <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#3C9B35] font-semibold">
                Amaia Steps Parking System
              </p>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.92] text-[#1F2933] max-w-3xl">
              Smart parking management for{" "}
              <span className="text-[#43A738]">
                modern residential communities.
              </span>
            </h1>

            <div className="mt-6 max-w-2xl space-y-4 text-[#263238] text-base sm:text-lg leading-relaxed">
              <p>
                Amaia Steps Parking System is a modern parking management platform
                designed to simplify vehicle access, visitor parking, slot
                allocation, billing, and parking operations across the property.
              </p>

              <p>
                Built with AI-ready architecture, the platform supports future
                integration with license plate recognition, smart barriers,
                digital payments, and real-time occupancy monitoring to enhance
                convenience, security, and operational efficiency.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl">
              {FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    className="bg-white/85 border border-[#E5DDC7] rounded-2xl p-5 shadow-[0_10px_30px_rgba(30,64,40,0.08)] backdrop-blur-sm"
                  >
                    <Icon size={26} className="text-[#43A738] mb-3" />
                    <p className="font-semibold text-[#2A3329] text-sm">{f.title}</p>
                    <p className="mt-2 text-xs text-[#65715F] leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center px-6 sm:px-10 lg:px-16 py-10">
          <div className="w-full max-w-xl bg-white/95 border border-[#E7E2D4] rounded-3xl shadow-[0_24px_70px_rgba(30,41,35,0.14)] p-8 sm:p-10 backdrop-blur-md relative overflow-hidden">
            <div className="absolute right-0 top-0 w-40 h-40 border border-[#E8DEC3] rounded-full translate-x-16 -translate-y-16 opacity-40" />
            <div className="absolute right-8 bottom-8 w-32 h-32 border border-[#E8DEC3] rounded-full opacity-30" />

            <div className="relative">
              <p className="text-xs font-mono uppercase tracking-[0.28em] text-[#3C9B35] font-semibold">
                Secure Sign-In
              </p>

              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3 text-[#1F2933]">
                Access the platform
              </h2>

              <p className="text-sm text-[#64705E] mt-2">
                Use your team credentials to continue.
              </p>

              <form className="mt-8 space-y-5" onSubmit={submit}>
                <div>
                  <Label
                    htmlFor="email"
                    className="text-xs font-mono uppercase tracking-widest text-[#3C9B35] font-semibold"
                  >
                    Email
                  </Label>

                  <div className="relative mt-2">
                    <EnvelopeSimple
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#70A667]"
                    />

                    <Input
                      id="email"
                      data-testid={TID.loginEmail}
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 bg-white border-[#E1D8C2] h-12 rounded-xl text-[#1F2933]"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="password"
                    className="text-xs font-mono uppercase tracking-widest text-[#3C9B35] font-semibold"
                  >
                    Password
                  </Label>

                  <div className="relative mt-2">
                    <Lock
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-[#70A667]"
                    />

                    <Input
                      id="password"
                      data-testid={TID.loginPassword}
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-11 bg-white border-[#E1D8C2] h-12 rounded-xl text-[#1F2933]"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid={TID.loginSubmit}
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-[#43A738] text-white hover:bg-[#368C2E] font-semibold shadow-[0_10px_20px_rgba(67,167,56,0.24)]"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-[#E7E2D4]">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#3C9B35] font-semibold mb-4">
                  Quick Demo Access
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {QUICK.map((q) => {
                    const Icon = q.icon;
                    return (
                      <button
                        key={q.role}
                        data-testid={TID.loginQuick(q.role)}
                        onClick={() =>
                          submit(null, {
                            email: q.email,
                            password: "Password@123",
                          })
                        }
                        className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-[#E1D8C2] bg-white text-sm font-medium text-[#2F3A30] hover:bg-[#F0F7EC] hover:border-[#B9D8B1] transition-colors"
                      >
                        <Icon size={17} className="text-[#43A738]" />
                        {q.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
